import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';

export interface Numerador {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly puntoDeVentaId: string;
  readonly puntoDeVenta: number;
  readonly sucursal: string;
  readonly ultimoNumero: string;
  readonly activo: boolean;
  readonly tipos: number;
}

interface PuntoDeVenta {
  readonly id: string;
  readonly numero: number;
  readonly nombre: string;
}

/**
 * Las cuatro cifras del punto de venta, como se imprimen: `0003`.
 *
 * Está afuera del JSX porque el lint —regla 3— rechaza literales adentro, y el cero de relleno es
 * un literal aunque no sea texto traducible.
 */
export function puntoDeVentaEnCuatroCifras(numero: number): string {
  return String(numero).padStart(4, '0');
}

/**
 * Cómo se nombra un talonario en una lista: `0003 · Facturas A`.
 *
 * Afuera del JSX porque el lint —regla 3— rechaza literales adentro, y el separador es un literal
 * aunque no sea texto traducible.
 */
export function etiquetaDeNumerador(punto: number, nombre: string): string {
  return `${puntoDeVentaEnCuatroCifras(punto)} · ${nombre}`;
}

/** Cómo se lee un número emitido: `0003-00001240`. */
export function numeroCompleto(punto: number, numero: string): string {
  return `${puntoDeVentaEnCuatroCifras(punto)}-${numero.padStart(8, '0')}`;
}

/**
 * Numeradores: los talonarios del comercio.
 *
 * ## Por qué es una pantalla propia
 *
 * Porque el dueño del producto lo dijo derecho: **dos numeradores fijos limitan al cien por
 * ciento**. Un comercio numera remitos, recibos, presupuestos, facturas, tickets, notas internas y
 * remitos no valorizados, y va a seguir inventando talonarios. Un numerador se crea acá, se nombra,
 * y después **cualquier tipo de comprobante lo elige**.
 *
 * ## Lo que la pantalla no deja hacer
 *
 * **Borrar uno.** Un numerador con comprobantes emitidos es la explicación de cómo se numeraron;
 * borrarlo dejaría los números sin dueño. Se desactiva, que es lo que de verdad se quiere decir.
 *
 * **Cambiarle el punto de venta.** Sus cuatro cifras están impresas en cada comprobante que emitió.
 * Lo que se quiere cuando alguien intenta eso es otro talonario.
 */
export function NumeradoresDeComprobante(): React.ReactElement {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  const puedeAdministrar = usePermiso('VENTAS.TIPO_DE_COMPROBANTE.ADMINISTRAR');
  const puedeFijar = usePermiso('VENTAS.NUMERADOR.FIJAR');

  const [creando, setCreando] = useState(false);
  const [formulario, setFormulario] = useState({ codigo: '', nombre: '', puntoDeVentaId: '' });
  const [corrigiendo, setCorrigiendo] = useState<Numerador | null>(null);
  const [edicion, setEdicion] = useState({ codigo: '', nombre: '' });
  const [fijando, setFijando] = useState<Numerador | null>(null);
  const [numeroNuevo, setNumeroNuevo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const numeradores = useQuery({
    queryKey: ['numeradores'],
    queryFn: () => pedir<Numerador[]>('/numeradores'),
  });
  const puntos = useQuery({
    queryKey: ['puntos-de-venta'],
    queryFn: () => pedir<PuntoDeVenta[]>('/puntos-de-venta'),
  });

  const refrescar = async (): Promise<void> => {
    setCreando(false);
    setCorrigiendo(null);
    setFijando(null);
    setError(null);
    await cola.invalidateQueries({ queryKey: ['numeradores'] });
  };
  const alFallar = (fallo: unknown): void => {
    setError(textoDeError(fallo));
  };

  const crear = useMutation({
    mutationFn: () => pedir<{ id: string }>('/numeradores', { method: 'POST', cuerpo: formulario }),
    onSuccess: refrescar,
    onError: alFallar,
  });

  const corregir = useMutation({
    mutationFn: () =>
      pedir<void>(`/numeradores/${corrigiendo?.id ?? ''}`, { method: 'PATCH', cuerpo: edicion }),
    onSuccess: refrescar,
    onError: alFallar,
  });

  const alternar = useMutation({
    mutationFn: (fila: Numerador) =>
      pedir<void>(`/numeradores/${fila.id}`, {
        method: 'PATCH',
        cuerpo: { activo: !fila.activo },
      }),
    onSuccess: refrescar,
    onError: alFallar,
  });

  const fijar = useMutation({
    mutationFn: () =>
      pedir<void>(`/numeradores/${fijando?.id ?? ''}/fijar`, {
        method: 'POST',
        cuerpo: { numero: numeroNuevo.trim() },
      }),
    onSuccess: refrescar,
    onError: alFallar,
  });

  const columnas: Columna<Numerador>[] = [
    {
      clave: 'codigo',
      titulo: t('numeradores.codigo'),
      contenido: (fila) => (
        <>
          <span className="numero">{fila.codigo}</span>
          <span className="block text-apoyo text-texto-tenue">{fila.nombre}</span>
        </>
      ),
    },
    {
      clave: 'punto',
      titulo: t('numeradores.puntoDeVenta'),
      // Las cuatro cifras, como se imprimen: es así como el comercio identifica el talonario.
      contenido: (fila) => (
        <>
          <span className="numero">{puntoDeVentaEnCuatroCifras(fila.puntoDeVenta)}</span>
          <span className="block text-apoyo text-texto-tenue">{fila.sucursal}</span>
        </>
      ),
    },
    {
      clave: 'va',
      titulo: t('numeradores.vaPor'),
      numerica: true,
      contenido: (fila) => (
        <span className="cifra">{numeroCompleto(fila.puntoDeVenta, fila.ultimoNumero)}</span>
      ),
    },
    {
      clave: 'tipos',
      titulo: t('numeradores.tiposQueLoUsan'),
      numerica: true,
      contenido: (fila) => <span className="cifra">{fila.tipos}</span>,
    },
    {
      clave: 'estado',
      titulo: t('numeradores.estado'),
      contenido: (fila) => (
        <Insignia tono={fila.activo ? 'bien' : 'neutro'}>
          {fila.activo ? t('numeradores.activo') : t('numeradores.inactivo')}
        </Insignia>
      ),
    },
    {
      clave: 'acciones',
      titulo: '',
      contenido: (fila) => (
        <div className="flex justify-end gap-1">
          {puedeFijar ? (
            <Boton
              onClick={() => {
                setFijando(fila);
                setNumeroNuevo(fila.ultimoNumero);
                setError(null);
              }}
            >
              {t('numeradores.fijar')}
            </Boton>
          ) : null}
          {puedeAdministrar ? (
            <>
              <Boton
                onClick={() => {
                  setCorrigiendo(fila);
                  setEdicion({ codigo: fila.codigo, nombre: fila.nombre });
                  setError(null);
                }}
              >
                {t('acciones.corregir')}
              </Boton>
              {/* No hay borrar: un numerador con comprobantes emitidos es la explicación de cómo
                  se numeraron, y borrarlo dejaría los números sin dueño. */}
              <Boton
                cargando={alternar.isPending}
                onClick={() => {
                  alternar.mutate(fila);
                }}
              >
                {fila.activo ? t('numeradores.desactivar') : t('numeradores.activar')}
              </Boton>
            </>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <Encabezado
        titulo={t('numeradores.titulo')}
        descripcion={t('numeradores.descripcion')}
        acciones={
          puedeAdministrar ? (
            <Boton
              tono="principal"
              onClick={() => {
                setFormulario({
                  codigo: '',
                  nombre: '',
                  puntoDeVentaId: puntos.data?.[0]?.id ?? '',
                });
                setCreando(true);
                setError(null);
              }}
            >
              {t('numeradores.nuevo')}
            </Boton>
          ) : undefined
        }
      />

      <Tarjeta>
        {numeradores.isPending ? <Cargando /> : null}
        {numeradores.isError ? <Problema error={numeradores.error} /> : null}
        {numeradores.isSuccess ? (
          <Tabla
            columnas={columnas}
            filas={numeradores.data}
            claveDeFila={(fila) => fila.id}
            vacio={
              <Vacio titulo={t('numeradores.vacio')} descripcion={t('numeradores.paraQueSirven')} />
            }
          />
        ) : null}
        {error === null ? null : (
          <p role="alert" className="mt-3 text-apoyo text-mal">
            {error}
          </p>
        )}
      </Tarjeta>

      <Dialogo
        abierto={creando}
        titulo={t('numeradores.nuevo')}
        onCerrar={() => {
          setCreando(false);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={crear.isPending}
            disabled={formulario.codigo.trim() === '' || formulario.nombre.trim() === ''}
            onClick={() => {
              crear.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('numeradores.codigo')}
            obligatorio
            value={formulario.codigo}
            maxLength={16}
            ayuda={t('numeradores.codigoAyuda')}
            onChange={(evento) => {
              setFormulario({ ...formulario, codigo: evento.target.value.toUpperCase() });
            }}
          />
          <Campo
            etiqueta={t('numeradores.nombre')}
            obligatorio
            value={formulario.nombre}
            maxLength={60}
            onChange={(evento) => {
              setFormulario({ ...formulario, nombre: evento.target.value });
            }}
          />
          {/* Se elige al crear y no se cambia después: las cuatro cifras quedan impresas en cada
              comprobante que emita. */}
          <Selector
            etiqueta={t('numeradores.puntoDeVenta')}
            obligatorio
            value={formulario.puntoDeVentaId}
            ayuda={t('numeradores.puntoAyuda')}
            onChange={(evento) => {
              setFormulario({ ...formulario, puntoDeVentaId: evento.target.value });
            }}
          >
            {(puntos.data ?? []).map((punto) => (
              <option key={punto.id} value={punto.id}>
                {etiquetaDeNumerador(punto.numero, punto.nombre)}
              </option>
            ))}
          </Selector>
          {error === null ? null : (
            <p role="alert" className="text-apoyo text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>

      <Dialogo
        abierto={corrigiendo !== null}
        titulo={t('numeradores.corregir')}
        descripcion={t('numeradores.puntoNoSeCambia')}
        onCerrar={() => {
          setCorrigiendo(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={corregir.isPending}
            onClick={() => {
              corregir.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('numeradores.codigo')}
            value={edicion.codigo}
            maxLength={16}
            onChange={(evento) => {
              setEdicion({ ...edicion, codigo: evento.target.value.toUpperCase() });
            }}
          />
          <Campo
            etiqueta={t('numeradores.nombre')}
            value={edicion.nombre}
            maxLength={60}
            onChange={(evento) => {
              setEdicion({ ...edicion, nombre: evento.target.value });
            }}
          />
          {error === null ? null : (
            <p role="alert" className="text-apoyo text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>

      <Dialogo
        abierto={fijando !== null}
        titulo={t('numeradores.fijar')}
        descripcion={fijando?.nombre ?? ''}
        onCerrar={() => {
          setFijando(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={fijar.isPending}
            disabled={numeroNuevo.trim() === ''}
            onClick={() => {
              fijar.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('numeradores.ultimoNumero')}
            value={numeroNuevo}
            inputMode="numeric"
            maxLength={12}
            ayuda={t('numeradores.fijarAyuda')}
            onChange={(evento) => {
              setNumeroNuevo(evento.target.value.replace(/\D/g, ''));
            }}
          />
          {error === null ? null : (
            <p role="alert" className="text-apoyo text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>
    </>
  );
}
